import { routeChatIdFromQuery } from '@/components/layout/Navigation/hooks/routeChatIdFromQuery';
import {
  Heidi2EvidenceTourRecentsStep,
  useIsHeidi2EvidenceTourOnRecents,
} from '@heidi/agents';
import {
  useAllWorkflowRuns,
  useCachedPendingApprovalRunIds,
  useProjectIdsNeedingAttention,
} from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import { SidebarGroup } from '@heidi/design-system/page-shell';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { navV3Messages } from '../nav-v3.messages';
import { matchesRecentsFilter, recentsFilterAtom } from './recents-filter';
import { RecentsSectionBody } from './recents-section-body';
import { RecentsSectionHeader } from './recents-section-header';
import { useRecentItemsSource } from './use-recent-items-source';

export const RECENTS_SKELETON_ROWS = 5;

export interface RecentsSectionProps {
  /**
   * 'sidebar' (default) — the full nav-v3 tray section: the heading, the
   * Search and three-dot filter header actions, and an unconstrained-height
   * list.
   * 'compact' — the Home hover preview's reuse of this same list: no
   * Search/filter chrome (the preview renders its own "Recents" heading
   * above it) and a collision-safe bounded scroll region so a long list can
   * never grow the popover past the viewport.
   */
  variant?: 'sidebar' | 'compact';
  /** Whether this mounted section owns the visits source's 30s poll. The
   *  transient Home preview disables it; the persistent sidebar keeps it. */
  pollVisits?: boolean;
}

/** Debounce window between a keystroke and the server search request it
 *  commits — short enough to feel live, long enough that a fast typer's
 *  intermediate keystrokes never each fire their own request. */
export const RECENTS_SEARCH_DEBOUNCE_MS = 300;

/**
 * "Recents" tray section — visits and chats merged into one
 * reverse-chronological list by `created_at`. Every loaded item renders
 * immediately (no five-item truncation or collapse); the always-available
 * three-dot header action opens a single-select All/Chats/Encounters filter,
 * held in `recentsFilterAtom` so it survives sidebar remounts and resets on
 * reload, defaulting to All. A second header
 * action opens an inline, unboxed search field that searches the user's full
 * chat and encounter history through the existing server search endpoints
 * (`useInfiniteQueryChats({ query })` / `useRecentVisits(sortBy,
 * { searchQuery })`), narrowed to whichever type filter is active — the
 * query text is debounced before it reaches either query and is never
 * logged or sent to analytics.
 *
 * While browsing (no query), each source loads, fails, and paginates
 * independently exactly as before: a slow or empty/errored source never
 * hides usable items already available from the other, and the whole
 * section hides only once both have settled with nothing to show at all.
 * While searching, only the source(s) the active filter allows are queried
 * at all — a "Chats" search never reaches the encounter endpoint and vice
 * versa — and the section instead shows a loading state until every
 * relevant source settles, then either the matching rows (with their own
 * independent Load more/pagination, unaffected by the query) or a localized
 * "No results" state with no Load more control.
 *
 * The active filter narrows both what renders and what Load more advances:
 * All advances whichever source(s) still have a next page, Chats advances
 * only chats, Encounters advances only visits. A source already fetching its
 * next page blocks a duplicate click for that source.
 */
export const RecentsSection = ({
  variant = 'sidebar',
  pollVisits = true,
}: RecentsSectionProps = {}) => {
  const intl = useIntl();
  const router = useRouter();
  const currentChatId = useMemo(() => {
    if (!router.pathname.includes('/scribe/chat')) return null;
    return routeChatIdFromQuery(router.query.id);
  }, [router.pathname, router.query.id]);
  const isCompact = variant === 'compact';
  const isTourOnRecents = useIsHeidi2EvidenceTourOnRecents();
  const [filter, setFilter] = useAtom(recentsFilterAtom);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // `query` drives the input value immediately, for responsive typing;
  // `searchQuery` is the debounced value actually passed to the search
  // queries, so a keystroke never fires its own request.
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSetSearchQuery = useDebouncedCallback(
    (value: string) => setSearchQuery(value),
    RECENTS_SEARCH_DEBOUNCE_MS
  );

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  // A keystroke landed but its debounced value has not committed yet — the
  // search queries below are still running (or about to run) against the
  // previous term.
  const isDebouncePending = isSearching && searchQuery !== trimmedQuery;

  // Which source(s) the active filter renders/paginates/searches: All uses
  // both, Chats/Encounters use only their own source. While searching, the
  // excluded source is not queried at all — a "Chats" search never reaches
  // the encounter search endpoint and vice versa.
  const includesVisits = filter !== 'chats';
  const includesChats = filter !== 'encounters';

  const {
    isLoading: isLoadingVisits,
    isPlaceholderData: isPlaceholderDataVisits,
    isFetching: isFetchingVisits,
    isError: isErrorVisits,
    hasNextPage: hasNextPageVisits,
    isFetchingNextPage: isFetchingNextPageVisits,
    fetchNextPage: fetchNextPageVisits,
    refetch: refetchVisits,
    items,
    availability,
    isLoadingChats,
    isFetchingChats,
    isErrorChats,
    hasNextPageChats,
    isFetchingNextPageChats,
    fetchNextPageChats,
    refetchChats,
  } = useRecentItemsSource({
    // `searchQuery` (unlike the raw `query` input) only ever holds a
    // committed, debounced value — it stays '' until the very first
    // debounce fires, so an in-flight keystroke never reaches the search
    // endpoint before its debounce window elapses.
    searchQuery,
    isSearching,
    visitsEnabled: !isSearching || includesVisits,
    chatsEnabled: !isSearching || includesChats,
    pollVisits,
  });
  const attentionProjectIds = useProjectIdsNeedingAttention();
  const { data: workflowRunsData } = useAllWorkflowRuns();
  const cachedAttentionRunIds = useCachedPendingApprovalRunIds(
    (workflowRunsData?.runs ?? []).map((run) => run.agent_run_id)
  );
  const effectiveAttentionProjectIds = useMemo(() => {
    const ids = new Set(attentionProjectIds);
    for (const run of workflowRunsData?.runs ?? []) {
      if (
        run.agent_run_id != null &&
        cachedAttentionRunIds.has(run.agent_run_id) &&
        run.project_id
      ) {
        ids.add(run.project_id);
      }
    }
    return ids;
  }, [attentionProjectIds, cachedAttentionRunIds, workflowRunsData?.runs]);
  const typeFilteredItems = useMemo(
    () => items.filter((item) => matchesRecentsFilter(item, filter)),
    [items, filter]
  );

  // `isLoading` (v5: `isPending && isFetching`) misses a *background* refetch
  // of an already-cached result — e.g. revisiting a search term whose empty
  // result is still in cache: `isLoading=false`, `isFetching=true`. A search
  // settlement check that only reads `isLoading`/`isPlaceholderData` would
  // treat that refetch as already settled and could flash "No results"
  // before it resolves. `isFetchingNextPage` is excluded so an explicit
  // Load-more click (a pagination fetch, not a re-fetch of the current
  // matches) never re-triggers the skeleton over already-rendered rows.
  const isVisitsUnsettled =
    includesVisits &&
    (isLoadingVisits ||
      isPlaceholderDataVisits ||
      (isFetchingVisits && !isFetchingNextPageVisits));
  const isChatsUnsettled =
    includesChats && (isLoadingChats || (isFetchingChats && !isFetchingNextPageChats));
  const isSearchUnsettled = isDebouncePending || isVisitsUnsettled || isChatsUnsettled;

  const isFilterLoading =
    (includesVisits && (isLoadingVisits || isPlaceholderDataVisits)) ||
    (includesChats && isLoadingChats);
  const isBrowseLoading =
    !isSearching && typeFilteredItems.length === 0 && isFilterLoading;
  const isSearchLoading = isSearching && isSearchUnsettled;
  const hasSearchError =
    (includesVisits && isErrorVisits) || (includesChats && isErrorChats);
  const isSearchEmpty =
    isSearching && !isSearchUnsettled && typeFilteredItems.length === 0;
  const showPartialSearchError =
    isSearching && hasSearchError && !isSearchUnsettled && !isSearchEmpty;
  const isBrowseFilterEmpty =
    !isSearching &&
    !isBrowseLoading &&
    !isSearchLoading &&
    typeFilteredItems.length === 0 &&
    items.length > 0;
  const hasNextPage =
    (includesVisits && Boolean(hasNextPageVisits)) ||
    (includesChats && Boolean(hasNextPageChats));
  // Preserve the shipped sidebar's filter-aware empty boundary. The compact
  // preview has no filter chrome and uses the shared all-sources availability
  // probe so it never opens for a genuinely empty account.
  const sectionHasNoData = isCompact
    ? !isSearching && availability === 'empty'
    : !isSearching &&
      items.length === 0 &&
      !isLoadingVisits &&
      !isLoadingChats &&
      !hasNextPage;
  const isFetchingNextPage =
    (includesVisits && isFetchingNextPageVisits) ||
    (includesChats && isFetchingNextPageChats);
  const showLoadMore =
    !isBrowseLoading && !isSearchLoading && !isSearchEmpty && hasNextPage;

  /** Fetches only the source(s) relevant to the active filter. Guarded so a
   *  click while that source's fetch is already in flight never starts a
   *  second, duplicate request for it. */
  const fetchMore = () => {
    if (isFetchingNextPage) return;
    if (includesVisits && hasNextPageVisits) void fetchNextPageVisits();
    if (includesChats && hasNextPageChats) void fetchNextPageChats();
  };

  const handleLoadMoreClick = () => {
    trackNavV3ButtonClicked({ label: 'nav_v3_recents_load_more' });
    fetchMore();
  };

  const handleFilterChange = (next: string) => {
    if (next !== 'all' && next !== 'chats' && next !== 'encounters') return;
    trackNavV3ButtonClicked({ label: `nav_v3_recents_filter_${next}` });
    setFilter(next);
  };

  const openSearch = () => {
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    debouncedSetSearchQuery.cancel();
    setIsSearchOpen(false);
    setQuery('');
    setSearchQuery('');
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQuery(next);
    const nextTrimmed = next.trim();
    if (nextTrimmed === '') {
      // Clearing (by the clear button or backspacing to empty) restores the
      // unfiltered browse results immediately — never worth a debounce wait.
      debouncedSetSearchQuery.cancel();
      setSearchQuery('');
    } else {
      debouncedSetSearchQuery(nextTrimmed);
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeSearch();
  };

  // The Heidi II tour still points at an empty Recents, so its heading stays
  // up for that step. The tour only ever anchors to the persistent sidebar.
  if (sectionHasNoData && (isCompact || !isTourOnRecents)) return null;

  return (
    // Compact's parent (`HomePreviewContent`) is already a flex column
    // bounded to a collision-safe max height for the whole popover —
    // `flex-1 min-h-0` claims whatever space the header rows above it
    // leave, and `max-h-96` caps it even when more space is available.
    // The Heidi II tour anchors only to the persistent sidebar's Recents
    // header, never the transient compact preview.
    <SidebarGroup
      data-testid={isCompact ? 'home-preview-recents-list' : 'sidebar-recents-section'}
      className={
        isCompact
          ? // Overrides the design system default `p-2`: `p-0` zeroes it (so
            // the date-group label and row icons below start flush with this
            // group's own left edge, matching the heading's inset — see
            // `home-preview-content.tsx`) and also removes the top padding
            // that used to double up with the heading row's old `gap-2`, then
            // `pb-2`/`pe-2` restore the bottom and trailing padding the
            // scroll region still wants. Asserted in
            // `home-preview-content.alignment.test.tsx`.
            'scrollbar-fade max-h-96 min-h-0 flex-1 overflow-y-auto p-0 pb-2 pe-2'
          : undefined
      }
    >
      {!isCompact && (
        <Heidi2EvidenceTourRecentsStep>
          <RecentsSectionHeader
            isSearchOpen={isSearchOpen}
            query={query}
            filter={filter}
            onFilterChange={handleFilterChange}
            onOpenSearch={openSearch}
            onCloseSearch={closeSearch}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleSearchKeyDown}
          />
        </Heidi2EvidenceTourRecentsStep>
      )}
      <RecentsSectionBody
        isCompact={isCompact}
        isSearching={isSearching}
        isSearchEmpty={isSearchEmpty}
        hasSearchError={hasSearchError}
        showPartialSearchError={showPartialSearchError}
        isBrowseFilterEmpty={isBrowseFilterEmpty}
        isBrowseLoading={isBrowseLoading}
        isSearchLoading={isSearchLoading}
        typeFilteredItems={typeFilteredItems}
        attentionProjectIds={effectiveAttentionProjectIds}
        includesVisits={includesVisits}
        includesChats={includesChats}
        isErrorVisits={isErrorVisits}
        isErrorChats={isErrorChats}
        refetchVisits={refetchVisits}
        refetchChats={refetchChats}
        skeletonRows={RECENTS_SKELETON_ROWS}
        currentChatId={currentChatId}
        loadMore={
          showLoadMore ? (
            <div className="flex justify-center py-1">
              <Button
                variant="ghost"
                size="xs"
                data-testid="sidebar-recents-load-more"
                onClick={handleLoadMoreClick}
                disabled={isFetchingNextPage}
              >
                {intl.formatMessage(navV3Messages.loadMore)}
              </Button>
            </div>
          ) : null
        }
      />
    </SidebarGroup>
  );
};
